import { useMemo } from 'react';
import { isValidTimeZone } from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { Field, Panel, inputCls } from '../ui/kit.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { allTimeZones } from '../components/FirstRun.tsx';
import { useSync } from '../sync/syncClient.ts';
import { useAuth } from '../auth/authStore.ts';
import { AccountBlock } from '../components/account/AccountWidgets.tsx';
import { LanguageSwitch, PaletteSwitch, ThemeSwitch } from '../components/ThemeControls.tsx';
import { useTheme } from '../ui/theme.ts';
import { useLang } from '../i18n/i18n.ts';
import { formatInstant } from '../i18n/format.ts';

export function SettingsView() {
  const { state, device, storageKind } = useStore();
  const store = getStore();
  const sync = useSync();
  const auth = useAuth();
  const theme = useTheme();
  const { t, persisted: langPersisted } = useLang();
  const s = state.settings;
  const profileLabel = auth.state.status === 'guest' ? t('Gäst (endast på den här enheten)', 'Guest (this device only)') : `${t('Konto', 'Account')} ${auth.state.profile.email ?? ''}`;
  const zones = useMemo(allTimeZones, []);
  const set = (patch: Partial<typeof s>) =>
    store
      .replace((st) => ({ ...st, settings: { ...st.settings, ...patch } }))
      .then(() => toast({ kind: 'ok', text: t('Inställningen sparad.', 'Setting saved.') }))
      .catch((e) => toast({ kind: 'error', text: errorText(e) }));

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 lg:px-8">
      <h1 className="text-2xl font-semibold tracking-tight">{t('Inställningar', 'Settings')}</h1>

      {/* Samma kontoåtgärder som i sidomenyn – nås även via mobilens navigeringsmeny. */}
      <AccountBlock variant="settings" />

      <Panel className="grid gap-4 p-5" aria-labelledby="st-appearance" id="utseende">
        <h2 id="st-appearance" className="text-base font-semibold">{t('Utseende', 'Appearance')}</h2>
        <div className="max-w-md">
          <ThemeSwitch />
        </div>
        <p className="-mt-2 text-sm text-muted">
          {theme.pref === 'system'
            ? t(`Följer enhetens inställning (just nu ${theme.resolved === 'light' ? 'ljust' : 'mörkt'}).`, `Follows the device setting (currently ${theme.resolved === 'light' ? 'light' : 'dark'}).`)
            : t('Gäller tills du väljer något annat.', 'Applies until you choose something else.')}
        </p>
        <PaletteSwitch />
        <p className="text-sm text-muted">
          {t(
            'Ljusläge och färgpalett väljs var för sig och sparas bara på den här enheten. De synkas inte med kontot och ändras inte när du loggar in eller ut. Dina kalenderfärger behålls.',
            'Light mode and colour palette are chosen separately and saved on this device only. They are not synced with the account and do not change when you sign in or out. Your calendar colours are kept.',
          )}
        </p>
        {(!theme.persisted || !theme.palettePersisted) && (
          <p className="rounded-lg border border-warn/60 bg-warn-bg px-3 py-2 text-sm" role="status">
            {t('Den här webbläsaren tillåter inte att valet sparas (till exempel privat läge). Det gäller tills sidan stängs.', 'This browser does not allow the choice to be saved (for example private mode). It applies until the page is closed.')}
          </p>
        )}
      </Panel>

      <Panel className="grid gap-4 p-5" aria-labelledby="st-language" id="sprak">
        <h2 id="st-language" className="text-base font-semibold">{t('Språk', 'Language')}</h2>
        <div className="max-w-md">
          <LanguageSwitch hideLegend />
        </div>
        <p className="text-sm text-muted">
          {t(
            'Språket gäller den här enheten. Det ändrar aldrig tidszon, klockslag, påminnelser eller texter du själv skrivit.',
            'The language applies to this device. It never changes time zone, times, reminders or text you have written yourself.',
          )}
        </p>
        {!langPersisted && (
          <p className="rounded-lg border border-warn/60 bg-warn-bg px-3 py-2 text-sm" role="status">
            {t('Språkvalet kan inte sparas i den här webbläsaren och gäller tills sidan stängs.', 'The language choice cannot be saved in this browser and applies until the page is closed.')}
          </p>
        )}
      </Panel>

      <Panel className="grid gap-4 p-5">
        <h2 className="text-base font-semibold">{t('Tid', 'Time')}</h2>
        <Field label={t('Tidszon', 'Time zone')} htmlFor="st-tz" hint={s.timeZoneConfirmed ? t('Bekräftad.', 'Confirmed.') : t('Ej bekräftad.', 'Not confirmed.')}>
          <select id="st-tz" className={inputCls} value={s.timeZone} onChange={(e) => isValidTimeZone(e.target.value) && void set({ timeZone: e.target.value, timeZoneConfirmed: true })}>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-sm text-muted">{t('Måndag som första veckodag, ISO-veckonummer och 24-timmarsklocka.', 'Monday as the first day of the week, ISO week numbers and a 24-hour clock.')}</p>
      </Panel>

      <Panel className="grid gap-4 p-5">
        <h2 className="text-base font-semibold">{t('Påminnelser', 'Reminders')}</h2>
        <label className="flex min-h-11 items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={s.hideSensitiveOnLockScreen} onChange={(e) => void set({ hideSensitiveOnLockScreen: e.target.checked })} />
          <span>
            {t('Dölj hälsouppgifter och företagsdetaljer på låsskärmen', 'Hide health and business details on the lock screen')}
            <span className="block text-xs text-muted">
              {t(
                'Notisen visar då bara "AutoDay-påminnelse" tills telefonen låses upp (Android: privat kanal; iPhone: neutral text).',
                'The notification then only shows "AutoDay reminder" until the phone is unlocked (Android: private channel; iPhone: neutral text).',
              )}
            </span>
          </span>
        </label>
        <label className="flex min-h-11 items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={s.summarizeImportedAllDay} onChange={(e) => void set({ summarizeImportedAllDay: e.target.checked })} />
          <span>
            {t('Sammanfatta importerade heldagsdatum som larmar samtidigt', 'Summarise imported all-day dates that alert at the same time')}
            <span className="block text-xs text-muted">{t('En notis med antal och den viktigaste – alla finns i appen. Tidsbokade aktiviteter sammanfattas aldrig.', 'One notification with the count and the most important one – all are in the app. Timed activities are never summarised.')}</span>
          </span>
        </label>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t('Tysta tider', 'Quiet hours')}</legend>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input type="checkbox" className="h-5 w-5" checked={s.quietHours.enabled} onChange={(e) => void set({ quietHours: { ...s.quietHours, enabled: e.target.checked } })} />
            {t('Skjut upp påminnelser under tysta tider', 'Postpone reminders during quiet hours')}
          </label>
          {s.quietHours.enabled && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('Från', 'From')} htmlFor="qh-s">
                <input id="qh-s" type="time" className={inputCls} value={s.quietHours.start} onChange={(e) => void set({ quietHours: { ...s.quietHours, start: e.target.value } })} />
              </Field>
              <Field label={t('Till', 'To')} htmlFor="qh-e">
                <input id="qh-e" type="time" className={inputCls} value={s.quietHours.end} onChange={(e) => void set({ quietHours: { ...s.quietHours, end: e.target.value } })} />
              </Field>
            </div>
          )}
          <p className="text-xs text-muted">{t('En påminnelse för något som börjar innan de tysta tiderna slutar (t.ex. läkartid 07.30) skickas ändå i tid.', 'A reminder for something that starts before the quiet hours end (for example a doctor’s appointment at 07:30) is still sent on time.')}</p>
        </fieldset>
        <Field label={t('Max snooze per påminnelse', 'Max snoozes per reminder')} htmlFor="st-snz">
          <input id="st-snz" type="number" min={1} max={10} className={inputCls} value={s.maxSnoozesPerPoint} onChange={(e) => void set({ maxSnoozesPerPoint: Math.min(10, Math.max(1, Number(e.target.value) || 1)) })} />
        </Field>
        <a href="#/notiser" className="inline-flex min-h-11 items-center text-sm font-medium text-accent hover:underline">
          {t('Påminnelsekoll och test →', 'Reminder check and test →')}
        </a>
      </Panel>

      <Panel className="grid gap-2 p-5 text-sm">
        <h2 className="text-base font-semibold">{t('Om data på denna enhet', 'About data on this device')}</h2>
        <p>
          {t('Aktiv lokal profil:', 'Active local profile:')} {profileLabel}. {t('Varje konto och gästläget har en egen lokal kopia på enheten.', 'Each account and the guest mode have their own local copy on the device.')}
        </p>
        <p className="text-muted">
          {t(
            'Ingen analys, ingen reklamspårning. Kalendertexter skickas bara till din egen AutoDay-server om du loggar in. Data krypteras i transit (TLS i drift), men AutoDay har varken end-to-end-kryptering eller egen kryptering av lokal data i vila.',
            'No analytics, no ad tracking. Calendar text is only sent to your own AutoDay server if you sign in. Data is encrypted in transit (TLS in production), but AutoDay has neither end-to-end encryption nor its own encryption of local data at rest.',
          )}
        </p>
        <details className="rounded-lg border border-border px-3 py-2">
          <summary className="min-h-9 cursor-pointer py-1 text-muted">{t('Avancerat (felsökning)', 'Advanced (troubleshooting)')}</summary>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted">{t('Lagring', 'Storage')}</dt>
            <dd>{storageKind === 'indexeddb' ? t('IndexedDB i webbläsaren', 'IndexedDB in the browser') : storageKind === 'sqlite' ? t('SQLite på enheten', 'SQLite on the device') : storageKind}</dd>
            <dt className="text-muted">{t('Enhets-ID', 'Device ID')}</dt>
            <dd className="break-all">
              <code>{device.deviceId}</code>
            </dd>
            <dt className="text-muted">{t('Synk (tekniskt)', 'Sync (technical)')}</dt>
            <dd>
              {sync.state}
              {sync.lastSyncAt ? t(', senast ', ', last ') + formatInstant(sync.lastSyncAt, state.settings.timeZone) : ''}
            </dd>
            <dt className="text-muted">{t('Webbadress', 'Web address')}</dt>
            <dd className="break-all">{location.origin}</dd>
          </dl>
        </details>
      </Panel>
    </div>
  );
}
